/**
 * @file SeoIndexNowCard.tsx
 * @description Карточка «Сообщать поисковикам об изменениях» (IndexNow) во
 * вкладке «Сайт → SEO → Основное» (phase19.16, партия «SEO-техника»,
 * v0.19.65; SEO7, SEO8, SEO16, SB145; макет uiux-seo-tech.md §3.2).
 *
 * Переключатель сохраняется сразу (`PATCH indexNowEnabled`), с отметкой
 * «изменено … · кто» и «Вернуть прежнее». Ниже — «Последняя отправка»
 * (когда · сколько адресов · итог словами по коду ответа) и до 10 адресов,
 * почему сейчас не отправляем (тестовая среда, сайт закрыт от поиска или на
 * работы, нет своего адреса), свёрнутый список «Адреса сайта и ключи».
 * Сервер до v0.19.65 блока `indexNow` не отдаёт — карточки нет.
 */

import { useState } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { SectionCard } from "../../ui/SectionCard";
import { StatusPill } from "../../ui/StatusPill";
import { Switch } from "../../ui/switch";
import { IndexNowKeysList } from "./IndexNowKeysList";
import { SeoFieldMark } from "./SeoFieldMark";
import type { SeoCardProps } from "./types";
import { timeAgo } from "../../../lib/site-builder/page-status";
import {
  INDEXNOW_BLOCK_TEXT,
  INDEXNOW_FOOTNOTE,
  INDEXNOW_NEVER_SENT,
  INDEXNOW_OFF_HINT,
  INDEXNOW_ON_HINT,
  INDEXNOW_SHOWN_URLS,
  indexNowResultView,
  moreUrlsText,
  urlsCountText,
} from "../../../lib/site-seo/indexnow-view";
import { errorCode, seoErrorText } from "../../../lib/site-seo/seo-errors";

export function SeoIndexNowCard({ api, settings, onHistory, readOnly }: SeoCardProps) {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const status = settings.indexNow;
  if (!status) return null;
  const { enabled, last, blockedBy, keys } = status;

  async function toggle(on: boolean) {
    setBusy(true);
    try {
      await api.save({ indexNowEnabled: on });
      notify.success(on ? "Поисковики будут узнавать об изменениях сразу." : "Отправка выключена. Поисковики узнают об изменениях при обычном обходе.");
    } catch (err) {
      // 409 «изменено в другом окне» уже сказан тостом и перечитан хуком настроек.
      if (errorCode(err) !== "site_seo_version_conflict") notify.error(seoErrorText(err, "Не удалось изменить отправку поисковикам."));
    } finally {
      setBusy(false);
    }
  }

  const result = last ? indexNowResultView(last) : null;
  const more = last ? moreUrlsText(last) : null;
  return (
    <SectionCard title="Сообщать поисковикам об изменениях">
      <label className="flex min-h-12 cursor-pointer items-center gap-3 has-[:disabled]:cursor-not-allowed">
        <Switch checked={enabled} disabled={readOnly || busy} onCheckedChange={(on) => void toggle(on)} />
        <span className="type-body text-on-surface">Сообщать поисковикам об изменениях</span>
      </label>
      <p className="type-caption text-on-surface-variant">{enabled ? INDEXNOW_ON_HINT : INDEXNOW_OFF_HINT}</p>
      <SeoFieldMark field="indexNowEnabled" settings={settings} onHistory={onHistory} readOnly={readOnly} />
      {enabled
        ? blockedBy.map((reason) => (
            <NoticeStrip key={reason} tone="warning">
              {INDEXNOW_BLOCK_TEXT[reason]}
            </NoticeStrip>
          ))
        : null}
      {enabled ? (
        <div className="flex min-w-0 flex-col gap-1" aria-label="Последняя отправка">
          <span className="text-sm font-medium text-on-surface">Последняя отправка</span>
          {last && result ? (
            <>
              <span className="type-body flex flex-wrap items-center gap-x-2 gap-y-1 text-on-surface">
                {timeAgo(last.at)} · {urlsCountText(last.count)}
                <StatusPill tone={result.tone}>{result.pill}</StatusPill>
              </span>
              {result.detail ? (
                result.tone === "neutral" ? (
                  <span className="type-caption text-on-surface-variant">{result.detail}</span>
                ) : (
                  <NoticeStrip tone={result.tone === "error" ? "error" : "warning"}>{result.detail}</NoticeStrip>
                )
              ) : null}
              {last.urls.length > 0 ? (
                <span className="type-caption break-all font-mono text-on-surface-variant">
                  {last.urls.slice(0, INDEXNOW_SHOWN_URLS).join(", ")}
                  {more ? ` ${more}` : ""}
                </span>
              ) : null}
            </>
          ) : (
            <span className="type-caption text-on-surface-variant">{INDEXNOW_NEVER_SENT}</span>
          )}
        </div>
      ) : null}
      <IndexNowKeysList keys={keys} />
      <p className="type-caption text-on-surface-variant">{INDEXNOW_FOOTNOTE}</p>
    </SectionCard>
  );
}
